// Copyright © 2019-2026 Focal Systems. All rights reserved.

export type PageContextKind = "chain" | "store" | "setting" | "section";

/** What the viewer is looking at. `path` is the pathname and doubles as identity. */
export interface PageContext {
  kind: PageContextKind;
  label: string;
  path: string;
}

export interface Step {
  id: string;
  label: string;
  status: "running" | "done";
}

export interface AnswerTable {
  columns: string[];
  rows: string[][];
}

/** `href` is an in-app path (optionally with a query), navigated client-side. */
export interface AnswerLink {
  label: string;
  href: string;
}

export type AssistantEvent =
  | { type: "step"; id: string; label: string; status: "running" | "done" }
  | { type: "text"; delta: string }
  | { type: "table"; table: AnswerTable }
  | { type: "link"; link: AnswerLink }
  | { type: "note"; text: string }
  | { type: "error"; message: string }
  | { type: "done" }
  /** Panel-only: the transport emits it from the `X-Request-Id` header; it is never parsed from the stream. */
  | { type: "requestId"; id: string };

export interface AssistantTurn {
  question: string;
  answer: string;
}

/** What the service accepts (AskRequest in focal-systems/bot-backoffice, src/assistant/models.py); more is refused with 422. */
export const MAX_QUESTION_CHARS = 2000;
export const MAX_ANSWER_CHARS = 8000;
export const MAX_HISTORY_TURNS = 10;
export const MAX_FEEDBACK_TABLES = 10;
export const MAX_TABLE_COLUMNS = 20;
export const MAX_TABLE_ROWS = 200;
export const MAX_CONTEXT_LABEL_CHARS = 200;
export const MAX_CONTEXT_PATH_CHARS = 500;
export const MAX_REQUEST_ID_CHARS = 64;
export const MAX_REASONS = 5;
export const MAX_REASON_CHARS = 60;
export const MAX_COMMENT_CHARS = 2000;

export interface AssistantRequest {
  question: string;
  context: PageContext | null;
  history: AssistantTurn[];
}

/** One question and its answer, as posted to Slack with a rating. */
export interface FeedbackPair {
  question: string;
  answer: string;
  tables: AnswerTable[];
  context: PageContext | null;
}

export interface RatingRequest {
  requestId: string | null;
  rating: "up" | "down";
  conversation: FeedbackPair[];
}

/** The seam a real backend (SSE) will implement. Must stop yielding once `signal` aborts. The feedback calls reject on a non-2xx response or a network failure. */
export interface AssistantTransport {
  ask(request: AssistantRequest, signal: AbortSignal): AsyncIterable<AssistantEvent>;
  /** Resolves the `feedback_id` the later comment and rating-change calls refer to. */
  sendRating(request: RatingRequest): Promise<string>;
  sendComment(feedbackId: string, reasons: string[], comment: string): Promise<void>;
  sendRatingEvent(feedbackId: string, rating: "up" | "down" | "none"): Promise<void>;
}

export type AnswerStatus = "working" | "streaming" | "done" | "stopped" | "error";

export interface UserMessage {
  id: string;
  role: "user";
  text: string;
}

export interface AssistantMessage {
  id: string;
  role: "assistant";
  question: string;
  context: PageContext | null;
  status: AnswerStatus;
  steps: Step[];
  text: string;
  tables: AnswerTable[];
  links: AnswerLink[];
  notes: string[];
  error: string | null;
  feedback: "up" | "down" | null;
  /** The service's `X-Request-Id` for this answer; absent on threads saved before ratings reached Slack. */
  requestId?: string | null;
  /** Set once the first rating reached the service; later changes and comments refer to it. */
  feedbackId?: string | null;
  feedbackState?: "sending" | "sent" | "failed" | null;
}

export interface DividerMessage {
  id: string;
  role: "divider";
  label: string;
}

export type Message = UserMessage | AssistantMessage | DividerMessage;
